import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import type { AuthUserResponse } from "@workspace/api-client-react";

export function useAuth() {
  const { data, error, status } = useQuery<AuthUserResponse | null>({
    queryKey: ["/api/auth/user"],
    // 401 resolves to null (definitively logged out). Any other failure —
    // network error, 5xx, load-induced fetch failure — throws, which keeps
    // the previously cached auth state instead of flipping to logged-out.
    queryFn: getQueryFn({ on401: "returnNull" }),
    // Retry transient failures aggressively with backoff. 401s never reach
    // retry because they resolve successfully to null.
    retry: 3,
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 3000),
    staleTime: 30000, // Consider auth data fresh for 30 seconds
    // A different tab can replace the browser session while this tab's auth
    // record is still within its 30-second freshness window.
    refetchOnWindowFocus: "always",
    // If all retries fail while we still have no answer, keep polling in the
    // background rather than giving up permanently.
    refetchInterval: (query) =>
      query.state.status === "error" && query.state.data === undefined ? 5000 : false,
  });

  // data === undefined  → no definitive answer yet (initial load OR transient
  //                       errors so far): stay in "loading" and never render
  //                       the logged-out screen.
  // data === null       → server said 401: definitively logged out.
  // data === object     → logged in.
  const isActuallyLoading = data === undefined;

  // All retries exhausted with no definitive answer: the server looks
  // unreachable. Background polling (refetchInterval) keeps retrying, so the
  // UI can show a "having trouble connecting" notice while staying in the
  // loading state.
  const hasConnectionTrouble = status === "error" && data === undefined;

  return {
    user: data?.user || null,
    tenant: data?.tenant || null,
    isLoading: isActuallyLoading,
    hasConnectionTrouble,
    isAuthenticated: !!data?.user,
    isAdmin: data?.user?.role === "admin",
    isMasterAdmin: data?.user?.isMasterAdmin || false,
    error: error,
  };
}
